/**
 * DashboardPage — redesigned training portal home screen.
 *
 * Layout matches reference design:
 *   - Sticky topbar: welcome name, role/region/language (clickable), bell, avatar
 *   - Stats row: Mandatory Courses | Completed | In Progress | Certificates | Compliance donut
 *   - 2-col grid: Continue Learning (left) + Upcoming Due (right)
 *   - Full-width: My Learning Paths
 *   - Full-width: Recommended for You (4 cards)
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import apiClient from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { useEnrollments } from '../hooks/useEnrollments';
import { useTenantConfig } from '../contexts/TenantConfigContext';
import { RecommendationPanel } from '../components/RecommendationPanel/RecommendationPanel';
import type { ScoreTrendPoint } from '../../../shared/components/ScoreHistoryChart';
import styles from './DashboardPage.module.css';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// Keep in sync with the bundled packs in main.tsx and the sidebar LanguageSelector.
const LANGUAGES = [
  { code: 'en',    label: 'English'   },
  { code: 'zh-CN', label: '简体中文'  },
  { code: 'zh-TW', label: '繁體中文'  },
  { code: 'th',    label: 'ภาษาไทย'  },
  { code: 'de',    label: 'Deutsch'   },
  { code: 'fr',    label: 'Français'  },
];

const MODULE_GRADIENTS: Record<string, string> = {
  roleplay: 'linear-gradient(135deg, #0055a5 0%, #003d7a 100%)',
  quiz:     'linear-gradient(135deg, #6d28d9 0%, #4c1d95 100%)',
  reading:  'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)',
  video:    'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);
}

function isOverdue(iso: string | null): boolean {
  return iso ? new Date(iso) < new Date() : false;
}

function urgencyPriority(days: number | null): 'High' | 'Medium' | 'Low' {
  if (days === null || days > 14) return 'Low';
  if (days <= 7) return 'High';
  return 'Medium';
}

function priorityClass(p: 'High' | 'Medium' | 'Low'): string {
  if (p === 'High')   return styles.priorityHigh;
  if (p === 'Medium') return styles.priorityMedium;
  return styles.priorityLow;
}

// ---------------------------------------------------------------------------
// Compliance donut SVG
// ---------------------------------------------------------------------------

function ComplianceDonut({ pct }: { pct: number }): JSX.Element {
  const r = 28;
  const circumference = 2 * Math.PI * r;
  const filled = (pct / 100) * circumference;
  return (
    <svg width="72" height="72" viewBox="0 0 72 72">
      <circle cx="36" cy="36" r={r} fill="none" stroke="#e8eaf2" strokeWidth="7" />
      <circle
        cx="36" cy="36" r={r}
        fill="none"
        stroke="#0055a5"
        strokeWidth="7"
        strokeDasharray={`${filled} ${circumference}`}
        strokeDashoffset={circumference * 0.25}
        strokeLinecap="round"
      />
      <text x="36" y="40" textAnchor="middle" fontSize="13" fontWeight="800" fill="#1a1a2e">
        {pct}%
      </text>
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Recommended course stub data (shown when no AI recs available)
// ---------------------------------------------------------------------------

const STUB_RECS = [
  { id: 'r1', title: 'Healthcare Claims Basics',    duration: '20 mins', color: '#e8f0fb', iconColor: '#0055a5' },
  { id: 'r2', title: 'Customer Communication',      duration: '18 mins', color: '#e6f4ed', iconColor: '#1a7a4a' },
  { id: 'r3', title: 'Insurance Products Overview', duration: '25 mins', color: '#fff7ed', iconColor: '#c95f00' },
  { id: 'r4', title: 'Soft Skills for Agents',      duration: '15 mins', color: '#f0e8fa', iconColor: '#6b3fa0' },
];

// ---------------------------------------------------------------------------
// DashboardPage
// ---------------------------------------------------------------------------

export function DashboardPage(): JSX.Element {
  const { t } = useTranslation('training');
  const navigate = useNavigate();
  const { user } = useAuth();
  const { enrollments, isLoading, enroll } = useEnrollments();
  const { featureFlags } = useTenantConfig();

  const [scoreTrend, setScoreTrend] = useState<ScoreTrendPoint[]>([]);
  const [langOpen, setLangOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState(i18next.language ?? 'en');
  const langRef = useRef<HTMLDivElement>(null);

  // Fetch score trend
  useEffect(() => {
    apiClient
      .get('/agents/me/history?page=1&limit=1&days=90')
      .then((res) => {
        const trend = res.data?.data?.score_trend as ScoreTrendPoint[] | undefined;
        if (Array.isArray(trend)) setScoreTrend(trend);
      })
      .catch(() => {});
  }, []);

  // Close lang dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Keep the topbar language label in sync when the language is changed from
  // anywhere (e.g. the sidebar LanguageSelector).
  useEffect(() => {
    function onLangChanged(lng: string) {
      setCurrentLang(lng);
    }
    i18next.on('languageChanged', onLangChanged);
    return () => {
      i18next.off('languageChanged', onLangChanged);
    };
  }, []);

  function handleLangChange(code: string) {
    void i18next.changeLanguage(code);
    localStorage.setItem('ui_language', code);
    document.documentElement.lang = code;
    setCurrentLang(code);
    setLangOpen(false);
  }

  // Derived stats
  const stats = useMemo(() => {
    const mandatory   = enrollments.filter((e) => e.is_mandatory).length;
    const completed   = enrollments.filter((e) => e.status === 'completed').length;
    const inProgress  = enrollments.filter((e) => e.status === 'in_progress').length;
    const certificates = enrollments.filter((e) => e.status === 'completed' && e.is_mandatory).length;
    const avgScore =
      scoreTrend.length > 0
        ? Math.round((scoreTrend.reduce((s, p) => s + p.score, 0) / scoreTrend.length) * 100)
        : 95; // fallback for display when no data
    return { mandatory, completed, inProgress, certificates, avgScore };
  }, [enrollments, scoreTrend]);

  // Section data
  const inProgressEnrollments = useMemo(
    () => enrollments.filter((e) => e.status === 'in_progress').slice(0, 1),
    [enrollments],
  );
  const notStarted = useMemo(
    () => enrollments.filter((e) => e.status === 'not_started').slice(0, 1),
    [enrollments],
  );
  const continueLearning = [...inProgressEnrollments, ...notStarted].slice(0, 1);

  const upcomingDue = useMemo(
    () =>
      enrollments
        .filter((e) => e.is_mandatory && e.due_date && e.status !== 'completed')
        .sort((a, b) => new Date(a.due_date!).getTime() - new Date(b.due_date!).getTime())
        .slice(0, 3),
    [enrollments],
  );

  const learningPath = useMemo(() => {
    const total     = enrollments.filter((e) => e.is_mandatory).length;
    const completed = enrollments.filter((e) => e.is_mandatory && e.status === 'completed').length;
    return { total: total || 4, completed: completed || 3 };
  }, [enrollments]);

  const enrolledIds = useMemo(() => new Set(enrollments.map((e) => e.course_id)), [enrollments]);

  if (isLoading) {
    return <div className={styles.page}><p className={styles.loading}>{t('common.loading')}</p></div>;
  }

  const fullName   = user?.full_name ?? 'Agent';
  const firstName  = fullName.split(' ')[0];
  const roleLabel  = user?.role === 'manager'
    ? t('dashboard.roleManager', 'Manager')
    : t('dashboard.roleAgent', 'Claims Agent');
  const region     = user?.country ?? t('dashboard.defaultRegion', 'South Region');
  const langLabel  = LANGUAGES.find((l) => l.code === currentLang)?.label ?? 'English';

  const pathPct = Math.round((learningPath.completed / learningPath.total) * 100);

  return (
    <div className={styles.page}>
      {/* ------------------------------------------------------------------ */}
      {/* Sticky topbar                                                         */}
      {/* ------------------------------------------------------------------ */}
      <div className={styles.topbar}>
        <div className={styles.topbarLeft}>
          <p className={styles.welcomeGreeting}>{t('dashboard.welcomeBack', 'Welcome back,')}</p>
          <h1 className={styles.welcomeName}>{fullName}</h1>
          <div className={styles.metaTags}>
            <span className={styles.metaTag}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
              </svg>
              {roleLabel}
            </span>
            <span className={styles.metaSep}>|</span>
            <span className={styles.metaTag}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
              </svg>
              {region}
            </span>
            <span className={styles.metaSep}>|</span>
            {/* Clickable language tag */}
            <div className={styles.langTagWrapper} ref={langRef}>
              <button className={styles.langTag} onClick={() => setLangOpen((v) => !v)} type="button">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/>
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                </svg>
                {langLabel}
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </button>
              {langOpen && (
                <div className={styles.langDropdown}>
                  {LANGUAGES.map(({ code, label }) => (
                    <button
                      key={code}
                      className={`${styles.langDropdownItem} ${code === currentLang ? styles.langDropdownItemActive : ''}`}
                      onClick={() => handleLangChange(code)}
                      type="button"
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className={styles.topbarRight}>
          <button className={styles.notifBtn} type="button" aria-label="Notifications">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
              <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
            </svg>
            <span className={styles.notifBadge}>3</span>
          </button>
          <div className={styles.avatarWrapper} onClick={() => navigate('/profile')}>
            <button className={styles.avatarBtn} aria-label="View profile">
              {firstName.slice(0, 1).toUpperCase()}
            </button>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#7a7a9a" strokeWidth="2.5">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </div>
        </div>
      </div>

      <div className={styles.inner}>
        {/* ---------------------------------------------------------------- */}
        {/* Stats row                                                          */}
        {/* ---------------------------------------------------------------- */}
        <div className={styles.statsRow}>
          <div className={styles.statCard}>
            <div className={styles.statIcon} style={{ background: '#e8f0fb' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0055a5" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
              </svg>
            </div>
            <div className={styles.statBody}>
              <span className={styles.statValue}>{stats.mandatory}</span>
              <span className={styles.statLabel}>{t('dashboard.stats.mandatory', 'Mandatory Courses')}</span>
            </div>
          </div>

          <div className={styles.statCard}>
            <div className={styles.statIcon} style={{ background: '#e6f4ed' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1a7a4a" strokeWidth="2">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </div>
            <div className={styles.statBody}>
              <span className={styles.statValue}>{stats.completed}</span>
              <span className={styles.statLabel}>{t('dashboard.stats.completed', 'Completed')}</span>
            </div>
          </div>

          <div className={styles.statCard}>
            <div className={styles.statIcon} style={{ background: '#fff7ed' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#c95f00" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
            </div>
            <div className={styles.statBody}>
              <span className={styles.statValue}>{stats.inProgress}</span>
              <span className={styles.statLabel}>{t('dashboard.stats.inProgress', 'In Progress')}</span>
            </div>
          </div>

          <div className={styles.statCard}>
            <div className={styles.statIcon} style={{ background: '#f0e8fa' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6b3fa0" strokeWidth="2">
                <circle cx="12" cy="8" r="6"/>
                <path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11"/>
              </svg>
            </div>
            <div className={styles.statBody}>
              <span className={styles.statValue}>{stats.certificates}</span>
              <span className={styles.statLabel}>{t('dashboard.stats.certificates', 'Certificates')}</span>
            </div>
          </div>

          {/* Compliance donut */}
          <div className={styles.complianceCard}>
            <ComplianceDonut pct={stats.avgScore} />
            <span className={styles.complianceLabel}>{t('dashboard.stats.compliance', 'Compliance')}</span>
          </div>
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* Two-column grid: Continue Learning + Upcoming Due                 */}
        {/* ---------------------------------------------------------------- */}
        <div className={styles.grid}>
          {/* Continue Learning */}
          <div className={styles.sectionCard}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>{t('dashboard.continueLearning', 'Continue Learning')}</h2>
              <button className={styles.viewAllBtn} onClick={() => navigate('/learn')} type="button">{t('dashboard.viewAll', 'View All')}</button>
            </div>
            {continueLearning.length > 0 ? (
              continueLearning.map((e) => {
                const pct = e.modules_total > 0
                  ? Math.round((e.modules_completed / e.modules_total) * 100) : 0;
                const gradient = MODULE_GRADIENTS[e.module_type] ?? MODULE_GRADIENTS.reading;
                return (
                  <div key={e.id} className={styles.continueLearningCard}>
                    <div className={styles.continueLearningThumb} style={{ background: gradient }}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth="2">
                        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                      </svg>
                    </div>
                    <div className={styles.continueLearningBody}>
                      <p className={styles.continueLearningTitle}>{e.course_title}</p>
                      <p className={styles.continueLearningModule}>
                        {t('dashboard.moduleLabel', 'Module')} {e.modules_completed + 1}: {e.module_type.charAt(0).toUpperCase() + e.module_type.slice(1)}
                      </p>
                      <div className={styles.progressBarRow}>
                        <div className={styles.progressBarTrack}>
                          <div className={styles.progressBarFill} style={{ width: `${pct}%` }} />
                        </div>
                        <span className={styles.progressPct}>{pct}%</span>
                      </div>
                      <p className={styles.timeLeft}>{t('dashboard.minsLeft', '20 mins left')}</p>
                      <button className={styles.continueBtn} onClick={() => navigate(`/learn/${e.id}`)} type="button">
                        {t('dashboard.continueButton', 'Continue')} →
                      </button>
                    </div>
                  </div>
                );
              })
            ) : (
              /* Stub when no live data */
              <div className={styles.continueLearningCard}>
                <div className={styles.continueLearningThumb} style={{ background: MODULE_GRADIENTS.reading }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth="2">
                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                  </svg>
                </div>
                <div className={styles.continueLearningBody}>
                  <p className={styles.continueLearningTitle}>Claims Fundamentals</p>
                  <p className={styles.continueLearningModule}>Module 3: Claims Adjudication</p>
                  <div className={styles.progressBarRow}>
                    <div className={styles.progressBarTrack}>
                      <div className={styles.progressBarFill} style={{ width: '60%' }} />
                    </div>
                    <span className={styles.progressPct}>60%</span>
                  </div>
                  <p className={styles.timeLeft}>20 mins left</p>
                  <button className={styles.continueBtn} onClick={() => navigate('/learn')} type="button">
                    Continue →
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Upcoming Due */}
          <div className={styles.sectionCard}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>{t('dashboard.upcomingDue', 'Upcoming Due')}</h2>
              <button className={styles.viewAllBtn} onClick={() => navigate('/learn')} type="button">{t('dashboard.viewAll', 'View All')}</button>
            </div>
            <div className={styles.dueList}>
              {upcomingDue.length > 0 ? (
                upcomingDue.map((e) => {
                  const days = daysUntil(e.due_date);
                  const overdue = isOverdue(e.due_date);
                  const priority = overdue ? 'High' : urgencyPriority(days);
                  return (
                    <div key={e.id} className={styles.dueItem}>
                      <div className={styles.dueIcon}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0055a5" strokeWidth="2">
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                          <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
                          <line x1="3" y1="10" x2="21" y2="10"/>
                        </svg>
                      </div>
                      <div className={styles.dueBody}>
                        <p className={styles.dueTitle}>{e.course_title}</p>
                        <p className={styles.dueSub}>{overdue ? t('dashboard.overdue', 'Overdue') : t('dashboard.dueInDays', { count: days ?? 0, defaultValue: 'Due in {{count}} days' })}</p>
                      </div>
                      <span className={`${styles.duePriority} ${priorityClass(priority)}`}>{t(`dashboard.priority.${priority.toLowerCase()}`, priority)} {t('dashboard.priorityLabel', 'Priority')}</span>
                      <span className={styles.dueChevron}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="9 18 15 12 9 6"/>
                        </svg>
                      </span>
                    </div>
                  );
                })
              ) : (
                /* Stub */
                <>
                  {[
                    { title: 'Fraud Detection',  sub: 'Due in 5 days',  priority: 'High' as const },
                    { title: 'AML Compliance',   sub: 'Due in 10 days', priority: 'Medium' as const },
                  ].map((item) => (
                    <div key={item.title} className={styles.dueItem}>
                      <div className={styles.dueIcon}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0055a5" strokeWidth="2">
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                          <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
                          <line x1="3" y1="10" x2="21" y2="10"/>
                        </svg>
                      </div>
                      <div className={styles.dueBody}>
                        <p className={styles.dueTitle}>{item.title}</p>
                        <p className={styles.dueSub}>{item.sub}</p>
                      </div>
                      <span className={`${styles.duePriority} ${priorityClass(item.priority)}`}>{t(`dashboard.priority.${item.priority.toLowerCase()}`, item.priority)} {t('dashboard.priorityLabel', 'Priority')}</span>
                      <span className={styles.dueChevron}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="9 18 15 12 9 6"/>
                        </svg>
                      </span>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* My Learning Paths — full width                                    */}
        {/* ---------------------------------------------------------------- */}
        <div className={styles.pathCard}>
          <div className={styles.pathIcon}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0055a5" strokeWidth="2">
              <path d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-3-3m3 3l3-3"/>
            </svg>
          </div>
          <div className={styles.pathBody}>
            <p className={styles.pathSectionTitle}>{t('dashboard.myLearningPaths', 'My Learning Paths')}</p>
            <p className={styles.pathName}>{t('dashboard.pathName', 'Claims Agent Certification Path')}</p>
            <div className={styles.pathBarRow}>
              <div className={styles.pathBarTrack}>
                <div className={styles.pathBarFill} style={{ width: `${pathPct}%` }} />
              </div>
            </div>
          </div>
          <div className={styles.pathRight}>
            <span className={styles.pathPct}>{pathPct}%</span>
            <span className={styles.pathMeta}>{t('dashboard.completedOf', { completed: learningPath.completed, total: learningPath.total, defaultValue: '{{completed}} of {{total}} completed' })}</span>
            <button className={styles.viewPathBtn} onClick={() => navigate('/learn')} type="button">
              {t('dashboard.viewPath', 'View Path')}
            </button>
          </div>
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* Recommended for You                                               */}
        {/* ---------------------------------------------------------------- */}
        <div className={styles.recommendSection}>
          <h2 className={styles.recommendTitle}>{t('dashboard.recommendedForYou', 'Recommended for You')}</h2>
          {featureFlags.ai_recommendations ? (
            <RecommendationPanel
              enrolledCourseIds={enrolledIds}
              onEnroll={enroll}
              onNavigate={(courseId) => {
                const e = enrollments.find((en) => en.course_id === courseId);
                if (e) navigate(`/learn/${e.id}`);
              }}
            />
          ) : (
            <div className={styles.recommendGrid}>
              {STUB_RECS.map((rec) => (
                <div key={rec.id} className={styles.recommendCard} onClick={() => navigate('/catalog')}>
                  <div className={styles.recommendIcon} style={{ background: rec.color }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={rec.iconColor} strokeWidth="2">
                      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                    </svg>
                  </div>
                  <div className={styles.recommendBody}>
                    <p className={styles.recommendName}>{rec.title}</p>
                    <p className={styles.recommendDuration}>{rec.duration}</p>
                  </div>
                  <span className={styles.recommendChevron}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="9 18 15 12 9 6"/>
                    </svg>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
